import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as dateFns from 'date-fns';

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  ButtonGroup,
  Button,
  FormInput,
  FormCheckbox,
  Collapse,
  ListGroup,
  ListGroupItem,
  Form,
  FormSelect,
} from 'shards-react';

import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { FiZoomIn, FiAlertCircle } from 'react-icons/fi';

import PageTitle from '../../components/common/PageTitle';
import LoadingContainer from '../../components/layout/LodingContainer';
import Pagination from '../../components/common/Pagination';
import ErrorBox from '../../components/common/ErrorBox';
import SearchSelect from '../../components/common/SearchSelect';

import { getRenotarizationLogsRequest } from '../../store/modules/renotarizationLogs/actions';
import { getAllInstitutionsRequest } from '../../store/modules/institution/actions';

import { scrollToTop, scrollMore } from '../../utils/scrolls';
import { useInput } from '../../hooks/input-hook';
import { dateAndHour } from '../../utils/dateFormatter';

const RESTAMP_STATUS = {
  success: 'Sucesso',
  error: 'Erro',
};

export default function RenotarizationLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const institutions = useSelector((state) => state.institution.institutions);
  const {
    documents,
    total: totalDocuments,
    loading,
  } = useSelector((state) => state.renotarizationLogs);

  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isAllInstitutions, setIsAllInstitutions] = useState(true);
  const [isAllOurNumbers, setIsAllOurNumbers] = useState(true);
  const [isAllPeriod, setIsAllPeriod] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isAllStatuses, setIsAllStatuses] = useState(true);
  const [showFilter, setShowFilter] = useState(true);
  const [error, setError] = useState(null);

  const { value: ourNumber, setValue: setOurNumber } = useInput('');
  const { value: initialDate, setValue: setInitialDate } = useInput('');
  const { value: finalDate, setValue: setFinalDate } = useInput('');

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsSize, setItemsSize] = useState(10);

  /*eslint-disable */
  useEffect(() => {
    dispatch(getAllInstitutionsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (total > 0) getLogs();
  }, [page, itemsSize]);
  /* eslint-enable */

  useEffect(() => {
    setTotal(totalDocuments);
  }, [documents]);

  function getLogs() {
    const query = {
      ...(!isAllInstitutions &&
        selectedInstitution && {
          clientId: selectedInstitution.client_id,
        }),
      ...(!isAllOurNumbers && ourNumber && { yourNumber: ourNumber }),
      ...(!isAllPeriod && { dateStart: initialDate, dateEnd: finalDate }),
      ...(!isAllStatuses && statusFilter && { restampResult: statusFilter }),
      page,
      limit: itemsSize,
    };
    dispatch(
      getRenotarizationLogsRequest(query, () => {
        setShowFilter(false);
        scrollMore(180);
      })
    );
  }

  function handleInstitutionFilter(inst) {
    setSelectedInstitution(inst);
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!isAllInstitutions && !selectedInstitution) {
      setError('Selecione uma instituição');
      return;
    }

    if (!isAllOurNumbers && (!ourNumber || ourNumber === '')) {
      setError(
        'Preencha o campo nosso número ou marque a opção para visualizar todos'
      );
      return;
    }

    if (
      !isAllPeriod &&
      initialDate &&
      initialDate !== '' &&
      finalDate &&
      finalDate !== '' &&
      dateFns.isAfter(
        dateFns.parseISO(initialDate),
        dateFns.parseISO(finalDate)
      )
    ) {
      setError('A data final precisa ser maior que a data inicial');
      return;
    }

    getLogs();
  }

  const displayDocuments = documents || [];

  const institutionByClientId = institutions
    ? Object.fromEntries(institutions.map((i) => [i.client_id, i]))
    : {};

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle
          sm="4"
          title="Registro de Renotarizações"
          subtitle="Logs"
          className="text-sm-left"
        />
      </Row>

      <Row id="form-container">
        <Col>
          <Card className="mb-3" small>
            <CardHeader className="border-bottom">
              <Row>
                <Col sm="6">
                  <h6 className="m-0 font-weight-bold">Filtros</h6>
                </Col>
                <Col sm="6" className="text-right">
                  <Button
                    theme="light"
                    onClick={() => setShowFilter(!showFilter)}
                  >
                    <span className="mr-2">
                      {showFilter ? <FaArrowUp /> : <FaArrowDown />}
                    </span>
                    {showFilter ? 'Esconder' : 'Mostrar'}
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <Collapse open={showFilter}>
              <ListGroup flush>
                <ListGroupItem className="p-3">
                  <Row>
                    <Col>
                      <Form onSubmit={handleSubmit}>
                        <Row form>
                          <Col md="6" className="form-group">
                            <span className="text-dark font-weight-bold">
                              Instituição
                            </span>
                            <Row className="pt-2">
                              <Col md="8">
                                <SearchSelect
                                  placeholder="Pesquisa por nome, sigla ou client_id"
                                  onSelect={handleInstitutionFilter}
                                  value={selectedInstitution?.value}
                                  options={
                                    institutions &&
                                    institutions.map((i) => ({
                                      label: `${i.name} - ${i.initials}`,
                                      value: i._id,
                                      client_id: i.client_id,
                                    }))
                                  }
                                  styleContainer={{ marginTop: '0px' }}
                                  disabled={isAllInstitutions}
                                />
                              </Col>
                              <Col md="4" className="pt-2 pl-0">
                                <FormCheckbox
                                  checked={isAllInstitutions}
                                  onChange={() => {
                                    setIsAllInstitutions(!isAllInstitutions);
                                    if (!isAllInstitutions)
                                      setSelectedInstitution(null);
                                  }}
                                >
                                  <span className="text-dark">Ver todas</span>
                                </FormCheckbox>
                              </Col>
                            </Row>
                          </Col>
                          <Col md="4" className="form-group">
                            <span className="text-dark font-weight-bold">
                              Seu número
                            </span>
                            <Row className="pt-2">
                              <Col md="8">
                                <FormInput
                                  type="text"
                                  value={ourNumber}
                                  onChange={(e) => setOurNumber(e.target.value)}
                                  disabled={isAllOurNumbers}
                                />
                              </Col>
                              <Col md="4" className="pt-2 pl-0">
                                <FormCheckbox
                                  checked={isAllOurNumbers}
                                  onChange={() => {
                                    setIsAllOurNumbers(!isAllOurNumbers);
                                    if (!isAllOurNumbers) setOurNumber('');
                                  }}
                                >
                                  <span className="text-dark">Ver todos</span>
                                </FormCheckbox>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md="2" className="form-group">
                            <span className="text-dark font-weight-bold">
                              Data inicial
                            </span>
                            <FormInput
                              type="date"
                              className="mt-2"
                              disabled={isAllPeriod}
                              value={initialDate}
                              onChange={(e) => setInitialDate(e.target.value)}
                              required={!isAllPeriod}
                            />
                          </Col>
                          <Col md="4" className="form-group">
                            <span className="text-dark font-weight-bold">
                              Data final
                            </span>
                            <Row className="pt-2">
                              <Col md="6">
                                <FormInput
                                  type="date"
                                  disabled={isAllPeriod}
                                  value={finalDate}
                                  onChange={(e) => setFinalDate(e.target.value)}
                                  required={!isAllPeriod}
                                />
                              </Col>
                              <Col md="6" className="pt-2 pl-0">
                                <FormCheckbox
                                  checked={isAllPeriod}
                                  onChange={() => setIsAllPeriod(!isAllPeriod)}
                                >
                                  <span className="text-dark">
                                    Intervalo completo
                                  </span>
                                </FormCheckbox>
                              </Col>
                            </Row>
                          </Col>
                          <Col md="4" className="form-group">
                            <span className="text-dark font-weight-bold">
                              Status
                            </span>
                            <Row className="pt-2">
                              <Col md="8">
                                <FormSelect
                                  value={statusFilter}
                                  onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                  }
                                  disabled={isAllStatuses}
                                  required={!isAllStatuses}
                                >
                                  <option value="" disabled>
                                    Escolher...
                                  </option>
                                  <option value="success">Sucesso</option>
                                  <option value="error">Erro</option>
                                </FormSelect>
                              </Col>
                              <Col md="4" className="pt-2 pl-0">
                                <FormCheckbox
                                  checked={isAllStatuses}
                                  onChange={() => {
                                    setIsAllStatuses(!isAllStatuses);
                                    if (!isAllStatuses) setStatusFilter('');
                                  }}
                                >
                                  <span className="text-dark">Ver todos</span>
                                </FormCheckbox>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md="8">
                            <ErrorBox error={error} />
                          </Col>
                          <Col md="4" className="text-right">
                            <Button className="btn-primary ml-3">
                              Consultar
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </Collapse>
          </Card>
        </Col>
      </Row>

      {loading && (
        <Row>
          <Col>
            <Card small className="mb-4">
              <CardBody className="p-0 pb-3 pl-3 pr-3 overflow-auto">
                <LoadingContainer loading={loading} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!loading && documents && (
        <Row>
          <Col>
            <Card small className="mb-4">
              <CardBody className="p-0 pb-3 pl-3 pr-3 overflow-auto">
                {displayDocuments.length > 0 ? (
                  <>
                    <table className="table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th scope="col" className="border-0">
                            #
                          </th>
                          <th scope="col" className="border-0">
                            Instituição
                          </th>
                          <th scope="col" className="border-0">
                            Seu número
                          </th>
                          <th scope="col" className="border-0">
                            Criado em
                          </th>
                          <th scope="col" className="border-0">
                            Status
                          </th>
                          <th scope="col" className="border-0">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayDocuments.map((doc, index) => (
                          <tr key={doc._id}>
                            <td>{index + 1}</td>
                            <td>
                              {institutionByClientId[doc.client_id]?.name ||
                                doc.client_id}
                            </td>
                            <td>{doc.your_number}</td>
                            <td>
                              {doc.date_time && dateAndHour(doc.date_time)}
                            </td>
                            <td>
                              {RESTAMP_STATUS[doc.restamp_result] ||
                                doc.restamp_result}
                            </td>
                            <td>
                              <ButtonGroup size="sm">
                                <Button
                                  theme="white"
                                  onClick={() =>
                                    navigate(
                                      `/logs/renotarization/${doc._id}`,
                                      { state: { doc } }
                                    )
                                  }
                                >
                                  <span className="text-light">
                                    <FiZoomIn />
                                  </span>{' '}
                                  Detalhes
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Pagination
                      total={total}
                      page={page}
                      items={itemsSize}
                      onChangePage={(e) => {
                        scrollToTop(e);
                        setPage(e);
                      }}
                      onChangeItems={(e) => {
                        scrollToTop();
                        setItemsSize(e);
                      }}
                    />
                  </>
                ) : (
                  <div className="col-sm-12 d-flex justify-content-center">
                    <h5 className="mt-3">
                      <FiAlertCircle className="mr-2" />
                      Não existem registros para os filtros selecionados
                    </h5>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}
